import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Droplet, Camera, AlertCircle, Check } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface WaterLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dailyTotal: number;
  dailyLimit: number;
}

interface WaterType {
  id: string;
  name: string;
  icon: string;
  description: string;
}

const waterTypes: WaterType[] = [
  {
    id: 'plain',
    name: 'Plain Water',
    icon: '💧',
    description: 'Regular water'
  },
  {
    id: 'mineral',
    name: 'Mineral Water',
    icon: '🌊',
    description: 'Natural mineral water'
  },
  {
    id: 'sparkling',
    name: 'Sparkling Water',
    icon: '✨',
    description: 'Carbonated water'
  },
  {
    id: 'flavored',
    name: 'Flavored Water',
    icon: '🍋',
    description: 'Water with natural flavors'
  }
];

const WaterLogModal: React.FC<WaterLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  dailyTotal,
  dailyLimit
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(250);
  const [selectedType, setSelectedType] = useState<string>('plain');
  const [photo, setPhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const presetAmounts = [250, 500, 1000];

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPhoto(e.target.files[0]);
    }
  };

  const resetForm = () => {
    setSelectedAmount(250);
    setSelectedType('plain');
    setPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    if (!photo) {
      toast.error(t('photoRequired'));
      return;
    }
    
    try {
      setLoading(true);
      
      if (dailyTotal + selectedAmount > 2500) {
        toast.error(t('limitReached'));
        return;
      }

      const timestamp = Date.now();
      const extension = photo.name.split('.').pop();
      const photoPath = `water-logs/${user.id}/${timestamp}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from('photos')
        .upload(photoPath, photo);

      if (uploadError) throw uploadError;
      
      const { error: logError } = await supabase
        .from('water_logs')
        .insert([
          { 
            user_id: user.id, 
            amount: selectedAmount,
            photo_url: photoPath,
            water_type: selectedType
          }
        ]);
      
      if (logError) throw logError;
      
      const { error: pointsError } = await supabase
        .from('user_points')
        .insert([
          { 
            user_id: user.id, 
            points: 5,
            source: 'water_log',
            description: `Water log: ${selectedAmount}ml (${waterTypes.find(t => t.id === selectedType)?.name})`
          }
        ]);
      
      if (pointsError) throw pointsError;
      
      toast.success(t('consumptionValidated'));
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error logging water:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const remainingAmount = 2500 - dailyTotal;
  const isLimitReached = dailyTotal >= 2500;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-t-xl sm:rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              <span className="sr-only">{t('close')}</span>
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <Droplet className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('validateConsumption')}
                </h3>

                {isLimitReached && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-xl flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
                    <p className="text-sm text-yellow-700">
                      {t('limitReached')}
                    </p>
                  </div>
                )}

                <div className="mt-6">
                  {/* Water Type Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('waterType')}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {waterTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setSelectedType(type.id)}
                          className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                            selectedType === type.id
                              ? 'border-blue-500 bg-blue-50 shadow-md transform scale-105'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <span className="text-2xl mr-2">{type.icon}</span>
                            <div className="text-left">
                              <p className="font-medium text-gray-900">{t(type.id + 'Water')}</p>
                              <p className="text-xs text-gray-500">{t(type.id + 'WaterDesc')}</p>
                            </div>
                            {selectedType === type.id && (
                              <Check className="h-5 w-5 text-blue-500 ml-auto" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('fixedAmount')}
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {presetAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => setSelectedAmount(amount)}
                          disabled={amount > remainingAmount}
                          className={`py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                            selectedAmount === amount
                              ? 'bg-blue-600 text-white shadow-md transform scale-105'
                              : amount > remainingAmount
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                          }`}
                        >
                          {amount} ml
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Photo Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('uploadPhoto')}
                    </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`w-full flex items-center justify-center px-4 py-4 border-2 border-dashed rounded-xl transition-all duration-200 ${
                        photo
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <Camera className={`h-6 w-6 mr-2 ${photo ? 'text-blue-500' : 'text-gray-400'}`} />
                      <span className={`font-medium ${photo ? 'text-blue-700' : 'text-gray-600'}`}>
                        {photo ? t('photoSelected') : t('uploadPhoto')}
                      </span>
                    </button>
                  </div>
                  
                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-500 mb-2">
                      <span>{t('dailyProgress')}</span>
                      <span>{dailyTotal} / 2500 ml</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${(dailyTotal / 2500) * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      onClick={handleSubmit}
                      disabled={loading || isLimitReached || !photo}
                      className="w-full sm:w-auto flex justify-center items-center px-6 py-3 rounded-xl border border-transparent shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? t('validating') : t('validate')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        resetForm();
                        onClose();
                      }}
                      className="mt-3 sm:mt-0 w-full sm:w-auto flex justify-center items-center px-6 py-3 rounded-xl border border-gray-300 shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterLogModal;